import HttpRequest from '@/../bower_components/teknisavuecomponents/assets/utils/HttpRequest';
import SnackbarUtils from '@/../bower_components/teknisavuecomponents/assets/utils/SnackbarUtils';
import ExpectedError from '@/../bower_components/teknisavuecomponents/assets/utils/ExpectedError';

/**
 * Classe para gerenciar e executar requisições HTTP em paralelo com limite de requisições simultâneas.
 */
export default class ParallelRequestHandler {
  /** Rota para as requisições HTTP */
  private route: string;
  /** Método HTTP a ser utilizado (get, post, put, delete) */
  private method: 'get' | 'post' | 'put' | 'delete';
  /** Indica se a tela deve mostrar um carregamento durante a requisição */
  private showLoading: boolean = false;

  /**
   * Cria uma instância do ParallelRequestHandler.
   * 
   * @param method - O método HTTP a ser utilizado (get, post, put, delete).
   * @param route - A rota para as requisições HTTP.
   * @param showLoading - Se a tela deve mostrar um carregamento durante a requisição.
   */
  constructor(method: 'get' | 'post' | 'put' | 'delete', route: string, showLoading: boolean) {
    this.method = method;
    this.route = route;
    this.showLoading = showLoading;
  }

  /**
   * Realiza uma requisição HTTP com os parâmetros fornecidos e lida com erros.
   * 
   * @param params - Parâmetros a serem enviados na requisição.
   * @returns A resposta da requisição.
   * @throws Erro se a requisição falhar.
   */
  private async makeRequest(params: any): Promise<any> {
    try {
      const response = await HttpRequest[this.method](this.route, params, this.showLoading);
      ExpectedError.throwResponseError(response);
      return response.data.dataset.data;
    } catch (err) {
      /*
      const defaultError = "Ocorreu um erro na requisição!";
      const message = err && (err as any).response ? (err as any).response.data.error : defaultError;
      SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ERROR);*/
      throw err;
    }
  }

  /**
   * Processa os chunks de dados em paralelo, respeitando o limite de requisições simultâneas.
   * 
   * @param chunks - Array de chunks de dados a serem processados.
   * @param generateParams - Função que gera os parâmetros para cada chunk.
   * @param limit - Número máximo de requisições simultâneas.
   * @returns Resultado de todas as requisições.
   */
  private async processChunks<T>(
    chunks: T[][],
    generateParams: (chunk: T[]) => any,
    limit: number
  ): Promise<any[]> {
    let results: any[] = [];
    for (let i = 0; i < chunks.length; i += limit) {
      const chunkSlice = chunks.slice(i, i + limit);
      const promises = chunkSlice.map(chunk => this.makeRequest(generateParams(chunk)));
      const chunkResults = await Promise.all(promises);
      results = results.concat(chunkResults.flat());
    }
    return results;
  }

  /**
   * Executa requisições em paralelo com base no array fornecido e no número máximo de requisições simultâneas.
   * 
   * @param array - Array de dados a serem enviados nas requisições.
   * @param maxRequests - Número máximo de requisições simultâneas.
   * @param generateParams - Função que gera os parâmetros para cada chunk de dados.
   * @returns Resultado de todas as requisições.
   */
  public async executeInParallel<T>(
    array: T[],
    generateParams: (chunk: T[]) => any,
    maxRequests: number = 5
  ): Promise<any[]> {
    // Garantir que o tamanho do chunk seja pelo menos 5
    const chunkSize = Math.max(Math.ceil(array.length / maxRequests), 5);
    const chunks = this.splitArray(array, chunkSize);
    const limit = Math.min(maxRequests, chunks.length);

    return this.processChunks(chunks, generateParams, limit);
  }

  /**
   * Divide um array em chunks de tamanho específico.
   * 
   * @param array - Array a ser dividido.
   * @param size - Tamanho de cada chunk.
   * @returns Array de chunks.
   */
  private splitArray<T>(array: T[], size: number): T[][] {
    const result: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  }
}
